'use client'

// Canonical save/unsave flow for listings — the ONE shared implementation
// behind every heart affordance (listing cards, map cards, saved-search rows,
// notifications queue, detail page, liked page). Consolidates what used to be
// three near-identical hook copies plus two inlined variants (fix wave F1).
//
// Guarantees:
// - Per-listing serialization: server operations for the same listing run
//   strictly in click order (module-level promise chain per id), so rapid
//   save / unsave / undo can never interleave their Firestore round-trips.
// - Planner preservation: saving a listing that already has a planner card
//   (notes, rating, tags) does NOT recreate the card — addListingToPlanner
//   would replace the existing server card with a blank 'maybe' one, wiping
//   the user's notes on resave.
// - One saved-ids sync per signed-in uid (useSyncSavedListingIds below), with
//   merge — not replace — semantics so a slow getSavedListingIds response
//   can't clobber likes made while it was in flight.

import * as React from 'react'
import { useAuth } from '@/components/auth'
import { useUserStore, usePlannerStore } from '@/lib/store'
import { emitGlobalToast } from '@/components/layout/global-toast'
import { useI18n } from '@/lib/i18n'
import {
  addListingToPlanner,
  deletePlannerCard,
  getSavedListingIds,
  saveListing,
  unsaveListing,
  updatePlannerCard,
} from '@/lib/firebase'
import type { PlannerCard } from '@/lib/types'

// Hard cap on the shortlist size (user decision 2026-07-16). Enforced at the
// user-initiated save action only — never in the low-level store setters, so
// server-sync merges of a legacy over-cap account aren't silently truncated.
export const MAX_LIKED_LISTINGS = 50

// ---------------------------------------------------------------------------
// Per-listing operation serialization

const opChains = new Map<string, Promise<unknown>>()

/** Queue `op` behind any in-flight save/unsave for the same listing. */
function runSerialized<T>(listingId: string, op: () => Promise<T>): Promise<T> {
  const prev = opChains.get(listingId) ?? Promise.resolve()
  // Run regardless of how the previous op settled.
  const next = prev.then(op, op)
  // Store a rejection-swallowing tail so a failed op doesn't poison the chain;
  // drop the map entry once the chain drains so the map can't grow unbounded.
  const tail = next.then(
    () => undefined,
    () => undefined
  )
  opChains.set(listingId, tail)
  tail.then(() => {
    if (opChains.get(listingId) === tail) opChains.delete(listingId)
  })
  return next
}

// ---------------------------------------------------------------------------
// Optimistic-op tracking — protects the saved-ids sync merge (see
// useSyncSavedListingIds). Both sets only ever hold ids the user acted on in
// THIS session on THIS device, and are cleared on sign-out.

/** Ids saved this session whose server write may postdate the sync fetch. */
const pendingSaves = new Set<string>()
/** Ids unsaved this session (mirror-image protection). */
const pendingUnsaves = new Set<string>()

// ---------------------------------------------------------------------------
// Imperative ops (exported for undo flows, e.g. components/liked)

/**
 * Save: optimistic store update immediately, Firestore writes serialized
 * behind any in-flight op for this listing. If a planner card already exists
 * it is left untouched (re-saving must never wipe notes/rating/tags).
 * `restoreCard` re-creates a specific prior card (Liked-page undo).
 * Errors are reverted and logged, never thrown.
 */
export function saveListingOp(
  uid: string,
  listingId: string,
  restoreCard?: PlannerCard
): Promise<void> {
  const { addSavedListing, removeSavedListing } = useUserStore.getState()
  pendingUnsaves.delete(listingId)
  pendingSaves.add(listingId)
  // Optimistic update
  addSavedListing(listingId)
  return runSerialized(listingId, async () => {
    let addedCard: PlannerCard | undefined
    try {
      await saveListing(uid, listingId)
      const existing = usePlannerStore.getState().getCardByListingId(listingId)
      if (!existing) {
        if (restoreCard) {
          // Undo of an unlike: re-create the ORIGINAL card (status, rating,
          // notes, customTags), not a blank 'maybe'.
          await updatePlannerCard(uid, restoreCard)
          addedCard = restoreCard
        } else {
          addedCard = await addListingToPlanner(uid, listingId, 'maybe')
        }
        usePlannerStore.getState().addCard(addedCard)
      }
      // Server write confirmed and already reflected in a completed sync?
      // Then the merge-protection entry is no longer needed.
      if (syncedUid === uid) pendingSaves.delete(listingId)
    } catch (error) {
      console.error('Error saving listing:', error)
      // Revert optimistic update
      pendingSaves.delete(listingId)
      removeSavedListing(listingId)
      if (addedCard) usePlannerStore.getState().removeCard(listingId)
    }
  })
}

/**
 * Unsave: optimistic removal immediately, Firestore deletes serialized behind
 * any in-flight op. Snapshots the planner card first so an error can restore
 * the original (not a blank). Errors are reverted and logged, never thrown.
 */
export function unsaveListingOp(uid: string, listingId: string): Promise<void> {
  const { addSavedListing, removeSavedListing } = useUserStore.getState()
  const snapshot = usePlannerStore.getState().getCardByListingId(listingId)
  pendingSaves.delete(listingId)
  pendingUnsaves.add(listingId)
  // Optimistic update
  removeSavedListing(listingId)
  usePlannerStore.getState().removeCard(listingId)
  return runSerialized(listingId, async () => {
    try {
      await unsaveListing(uid, listingId)
      await deletePlannerCard(uid, listingId)
      // Re-remove locally (idempotent): a save queued BEFORE this unsave may
      // have re-added its card after our optimistic removal above ran.
      usePlannerStore.getState().removeCard(listingId)
      if (syncedUid === uid) pendingUnsaves.delete(listingId)
    } catch (error) {
      console.error('Error unsaving listing:', error)
      // Revert optimistic update
      pendingUnsaves.delete(listingId)
      addSavedListing(listingId)
      if (snapshot) usePlannerStore.getState().addCard(snapshot)
    }
  })
}

// ---------------------------------------------------------------------------
// Saved-ids sync — one Firestore fetch per signed-in uid, however many
// components mount it (module-level ref keyed by uid).

let syncStartedUid: string | null = null
let syncedUid: string | null = null
const syncListeners = new Set<() => void>()

function emitSync() {
  syncListeners.forEach((l) => l())
}
function subscribeSync(cb: () => void) {
  syncListeners.add(cb)
  return () => {
    syncListeners.delete(cb)
  }
}
const getSyncedUid = () => syncedUid
const getServerSyncedUid = () => null

function startSavedIdsSync(uid: string) {
  if (syncStartedUid === uid) return
  // Direct uid switch (defensive — normally sign-out passes through null
  // first, which resets everything below): drop the old user's protection
  // sets so their in-session ops can't leak into the new user's merge.
  if (syncStartedUid !== null) {
    pendingSaves.clear()
    pendingUnsaves.clear()
  }
  syncStartedUid = uid
  if (syncedUid !== null && syncedUid !== uid) {
    syncedUid = null
    emitSync()
  }
  getSavedListingIds(uid)
    .then((serverIds) => {
      if (syncStartedUid !== uid) return // signed out / switched mid-flight
      useUserStore
        .getState()
        .mergeSavedListingIds(serverIds, [...pendingSaves], [...pendingUnsaves])
      // Ids the server already reflects no longer need merge protection.
      const serverSet = new Set(serverIds)
      for (const id of [...pendingSaves]) if (serverSet.has(id)) pendingSaves.delete(id)
      for (const id of [...pendingUnsaves]) if (!serverSet.has(id)) pendingUnsaves.delete(id)
      syncedUid = uid
      emitSync()
    })
    .catch((error) => {
      console.error('Error syncing saved listing ids:', error)
      if (syncStartedUid === uid) syncStartedUid = null // allow a later retry
    })
}

/**
 * Sync the signed-in user's saved listing ids from Firestore into the user
 * store — exactly once per uid. Exposes `hydrated` (true once the current
 * uid's ids have been merged).
 *
 * Merge semantics / tradeoff: the fetched list is applied as
 * (serverIds ∪ in-session optimistic saves) − in-session optimistic unsaves —
 * NOT a plain replace. A plain replace let a slow fetch response clobber
 * likes made while it was in flight (the verified data-loss bug). The cost of
 * the union: an unsave made on ANOTHER device mid-session, for an id this
 * device also saved/unsaved this session, isn't reflected until the local op
 * confirms or the next sign-in sync — acceptable versus losing in-session
 * likes. (A plain union with ALL current local ids would be wrong for
 * cross-device unsaves generally; the pending sets keep the union scoped to
 * ids this session actually touched.)
 */
export function useSyncSavedListingIds(): { hydrated: boolean } {
  const { user } = useAuth()
  const currentSyncedUid = React.useSyncExternalStore(
    subscribeSync,
    getSyncedUid,
    getServerSyncedUid
  )

  React.useEffect(() => {
    if (!user) {
      // Signed out: reset so the next sign-in re-syncs, and drop the
      // per-session protection sets.
      if (syncStartedUid !== null || syncedUid !== null) {
        syncStartedUid = null
        syncedUid = null
        pendingSaves.clear()
        pendingUnsaves.clear()
        emitSync()
      }
      return
    }
    startSavedIdsSync(user.uid)
  }, [user])

  return { hydrated: !!user && currentSyncedUid === user.uid }
}

// ---------------------------------------------------------------------------
// The hook

/**
 * Shared save/unsave toggle: auth-gated (prompts sign-in), optimistic with
 * revert on error, planner board kept in sync, per-listing ops serialized.
 * Also mounts the once-per-uid saved-ids sync so `isSaved` is trustworthy
 * wherever a heart renders.
 */
export function useSaveListing(listingId: string) {
  const { user, signIn } = useAuth()
  const { t } = useI18n()
  const savedListingIds = useUserStore((s) => s.savedListingIds)
  const [isSaving, setIsSaving] = React.useState(false)
  useSyncSavedListingIds()

  const isSaved = savedListingIds.includes(listingId)

  // Cap the shortlist. A new like past the limit is refused (existing likes and
  // unlikes are unaffected — a legacy over-cap account just can't add more).
  const atLimit = !isSaved && savedListingIds.length >= MAX_LIKED_LISTINGS
  const notifyLimit = React.useCallback(() => {
    emitGlobalToast(
      t('liked.limitReached', 'You’ve reached the {n} liked-homes limit — remove one to add another.').replace(
        '{n}',
        String(MAX_LIKED_LISTINGS)
      )
    )
  }, [t])

  const save = React.useCallback(async () => {
    if (!listingId) return
    if (!user) {
      signIn()
      return
    }
    if (atLimit) {
      notifyLimit()
      return
    }
    await saveListingOp(user.uid, listingId)
  }, [user, signIn, listingId, atLimit, notifyLimit])

  const unsave = React.useCallback(async () => {
    if (!listingId) return
    if (!user) {
      signIn()
      return
    }
    await unsaveListingOp(user.uid, listingId)
  }, [user, signIn, listingId])

  const toggleSave = React.useCallback(
    async (e?: React.MouseEvent) => {
      e?.preventDefault()
      e?.stopPropagation()
      if (isSaving || !listingId) return
      if (!user) {
        signIn()
        return
      }
      // Liking a new listing while at the cap is blocked; unliking always works.
      if (!isSaved && atLimit) {
        notifyLimit()
        return
      }
      setIsSaving(true)
      try {
        if (isSaved) {
          await unsaveListingOp(user.uid, listingId)
        } else {
          await saveListingOp(user.uid, listingId)
        }
      } finally {
        setIsSaving(false)
      }
    },
    [isSaving, listingId, user, signIn, isSaved, atLimit, notifyLimit]
  )

  return { isSaved, isSaving, toggleSave, save, unsave, atLimit }
}
